import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils/utils';
import { Check } from 'lucide-react';

interface ChooseListOption {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  isTeamMember?: boolean;
}

interface BaseChooseListInputProps {
  multiSelect?: boolean;
  options: ChooseListOption[];
  columns?: 1 | 2 | 3;
  title?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  variant?: 'card' | 'chatbot' | 'compact';
}

interface ChooseListInputSingleProps extends BaseChooseListInputProps {
  multiSelect?: false;
  value?: string;
  onChange: (value: string) => void;
}

interface ChooseListInputMultiProps extends BaseChooseListInputProps {
  multiSelect: true;
  value?: string[];
  onChange: (value: string[]) => void;
}

type ChooseListInputProps =
  | ChooseListInputSingleProps
  | ChooseListInputMultiProps;

export const ChooseListInput: React.FC<ChooseListInputProps> = ({
  options,
  value,
  onChange,
  columns = 1,
  title,
  description,
  multiSelect,
  required = false,
  disabled = false,
  className = '',
  variant = 'card',
}) => {
  const { t } = useTranslation('userSelector');
  // Normalize value to array for easier handling
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

  const handleSelectionChange = (optionId: string) => {
    if (multiSelect) {
      const newSelection = selectedValues.includes(optionId)
        ? selectedValues.filter((id) => id !== optionId)
        : [...selectedValues, optionId];
      onChange(newSelection);
    } else {
      onChange(optionId);
    }
  };

  const isSelected = (optionId: string) => selectedValues.includes(optionId);

  const getGridClasses = () => {
    switch (columns) {
      case 1:
        return 'grid-cols-1';
      case 2:
        return 'grid-cols-1 sm:grid-cols-2';
      case 3:
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
      default:
        return 'grid-cols-1';
    }
  };

  // Split options into full-width and regular options
  const fullWidthOptions = options.filter((option) => option.fullWidth);
  const regularOptions = options.filter((option) => !option.fullWidth);

  // Render option card (card variant)
  const renderOptionCard = (option: ChooseListOption) => (
    <div
      key={option.id}
      className={cn(
        'group cursor-pointer transition-all duration-300 rounded-xl bg-white shadow-sm hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-0.5 relative',
        option.disabled || disabled
          ? 'cursor-not-allowed opacity-50'
          : isSelected(option.id)
          ? 'ring-2 ring-primary shadow-md shadow-primary/20'
          : 'ring-1 ring-border/50 hover:ring-primary/40',
        // Team member highlighting - accent border on left/top
        option.isTeamMember && 'border-s-2 border-primary'
      )}
      onClick={() =>
        !disabled && !option.disabled && handleSelectionChange(option.id)
      }
    >
      <div className="flex items-center p-5 overflow-hidden">
        {/* Selection indicator for multi-select */}
        {multiSelect && (
          <div className="me-4 z-10">
            <div
              className={cn(
                'w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all',
                isSelected(option.id)
                  ? 'bg-primary border-primary shadow-sm'
                  : 'border-muted-foreground/30 group-hover:border-primary/50 group-hover:scale-105'
              )}
            >
              {isSelected(option.id) && (
                <Check className="w-3 h-3 text-primary-foreground stroke-[3]" />
              )}
            </div>
          </div>
        )}

        {/* Icon section */}
        {option.icon && (
          <div className="me-4 z-10 flex-shrink-0">
            <div
              className={cn(
                'w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200',
                isSelected(option.id)
                  ? 'bg-primary/15 text-primary'
                  : 'bg-muted/50 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
              )}
            >
              {option.icon}
            </div>
          </div>
        )}

        {/* Decorative dot indicator */}
        {!option.icon && !multiSelect && (
          <div className="me-4 flex-shrink-0">
            <div
              className={cn(
                'w-2 h-2 rounded-full transition-all duration-300',
                isSelected(option.id)
                  ? 'bg-primary scale-125'
                  : 'bg-muted-foreground/20 group-hover:bg-primary/40 group-hover:scale-110'
              )}
            />
          </div>
        )}

        {/* Content section */}
        <div className="flex-1">
          <div className="flex items-center gap-2 justify-between">
            <div
              className={cn(
                'text-base transition-colors',
                isSelected(option.id)
                  ? 'text-primary'
                  : 'text-foreground/90 group-hover:text-foreground'
              )}
            >
              {option.title}
            </div>
            {/* Team member badge */}
            {option.isTeamMember && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                {t('teamMember')}
              </span>
            )}
          </div>
          {option.description && (
            <div className="text-sm text-muted-foreground mt-1.5 leading-relaxed font-light">
              {option.description}
            </div>
          )}
        </div>

        {/* Selection indicator for single select */}
        {!multiSelect && isSelected(option.id) && (
          <Check className="w-3 h-3 text-primary-foreground stroke-[3]" />
        )}
      </div>
    </div>
  );

  return (
    <div className={cn('w-full', className)}>
      {(title || description) && (
        <div className="mb-6">
          {title && (
            <h3 className="text-lg font-semibold text-foreground mb-2 block">
              {title}
              {required && <span className="text-destructive ml-1">*</span>}
            </h3>
          )}
          {description && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {description}
            </p>
          )}
        </div>
      )}

      {variant === 'chatbot' ? (
        // Chatbot variant - modern pill-style buttons for conversational UI
        <div className="flex flex-wrap gap-2.5">
          {options.map((option) => (
            <button
              key={option.id}
              onClick={() =>
                !disabled &&
                !option.disabled &&
                handleSelectionChange(option.id)
              }
              disabled={option.disabled || disabled}
              className={cn(
                'rounded-xl px-5 py-2.5 transition-all duration-200 font-medium text-[15px] shadow-sm hover:shadow-md relative',
                option.disabled || disabled
                  ? 'cursor-not-allowed opacity-40 bg-gray-100 text-gray-400'
                  : isSelected(option.id)
                  ? 'bg-blue-600 hover:bg-blue-700 text-white scale-[1.02]'
                  : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:border-gray-300 hover:scale-[1.02]',
                // Team member highlighting - accent border
                option.isTeamMember &&
                  !isSelected(option.id) &&
                  'border-primary/50'
              )}
            >
              <span className="flex items-center gap-2">
                {option.title}
                {option.isTeamMember && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                    {t('teamMember')}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      ) : variant === 'compact' ? (
        // Compact variant - less padding, no icons
        <div className={cn('grid gap-3', getGridClasses())}>
          {options.map((option) => (
            <div
              key={option.id}
              className={cn(
                'group cursor-pointer transition-all duration-300 rounded-lg bg-white shadow-sm hover:shadow-md hover:shadow-primary/10 relative',
                option.disabled || disabled
                  ? 'cursor-not-allowed opacity-50'
                  : isSelected(option.id)
                  ? 'ring-2 ring-primary shadow-md shadow-primary/20'
                  : 'ring-1 ring-border/50 hover:ring-primary/40',
                // Team member highlighting - accent border on left
                option.isTeamMember && 'border-s-2 border-primary'
              )}
              onClick={() =>
                !disabled &&
                !option.disabled &&
                handleSelectionChange(option.id)
              }
            >
              <div className="flex items-center px-4 py-3 overflow-hidden">
                {/* Decorative dot indicator */}
                {!multiSelect && (
                  <div className="me-3 flex-shrink-0">
                    <div
                      className={cn(
                        'w-2 h-2 rounded-full transition-all duration-300',
                        isSelected(option.id)
                          ? 'bg-primary scale-125'
                          : 'bg-muted-foreground/20 group-hover:bg-primary/40 group-hover:scale-110'
                      )}
                    />
                  </div>
                )}

                {/* Content section */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        'text-sm transition-colors',
                        isSelected(option.id)
                          ? 'text-primary font-medium'
                          : 'text-foreground/90 group-hover:text-foreground'
                      )}
                    >
                      {option.title}
                    </div>
                    {/* Team member badge */}
                    {option.isTeamMember && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                        {t('teamMember')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Regular options in grid */}
          {regularOptions.length > 0 && (
            <div className={cn('grid gap-4', getGridClasses())}>
              {regularOptions.map(renderOptionCard)}
            </div>
          )}

          {/* Full-width options */}
          {fullWidthOptions.map(renderOptionCard)}
        </div>
      )}
    </div>
  );
};

export default ChooseListInput;
